import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useHistory } from "react-router-dom";

import { updateApplication } from '../../../actions/applications';
import { getRoute, getSession, loading } from '../../../actions/sessions';
import {asyncActions, handleAsyncError} from '../../../actions/general';
import { validateBirthDate } from '../../../actions/users';
import { states, formStyles, poBoxRegex } from '../../../constants/SiteVariables';
import ApplicationRecord from '../../../records/application';
import useRouteStatus from '../../../hooks/useRouteStatus';
import useValidateRoute from '../../../hooks/useValidateRoute';
import useSessionEntity from '../../../hooks/useSessionEntity';
import Error from '../../shared/Error';
import Loading from '../../shared/Loading';
import ColumnInput from '../../shared/ColumnInput';
import StickyInput from '../../shared/StickyInput';
import StickySelect from '../../shared/StickySelect';
import Tooltip from '../../shared/Tooltip';

export default function Personal({location}) {
  const dispatch = useDispatch();
  const history = useHistory();
  const isLoading = useSelector(state => state.sessions.get('loading'));
  const application = useSessionEntity('application');
  const user = useSessionEntity('user');
  const session = useSelector(state => state.sessions.get('session'));
  const asyncErrors = useSelector(state => state.general.get('errors'));
  const [foundUser, setFoundUser] = useState(false);

  useRouteStatus(location);

  useEffect(() => {
    if (foundUser) history.push("/init/existing_user");
  }, [foundUser]);

  useEffect(() => {
    if (asyncErrors.count() > 0 && asyncErrors.find(e => e && e.includes('Phone number already in use'))) {
      setFoundUser(true);
      dispatch(loading(false));
    }
  }, [asyncErrors]);

  const checkBirthdate = async (val) => {
    try {
      const res = await dispatch(validateBirthDate(val));
      return res.result;
    } catch (e) {
      dispatch(handleAsyncError(e));
    }
  }

  const save = values => {
    dispatch(loading(true));

    return dispatch(asyncActions(
      updateApplication(new ApplicationRecord(values)),
      getSession({ cart: false, prequal: false, merchant: false }),
      getRoute()
    ))
    .then(res => dispatch(loading(false)));
  }


  if (!useValidateRoute({ apply: true, company: true })) return (<></>);

  if (!user) return (<Loading active={true} />);

  return (
    <div id="content">
      <div className="has-text-centered mb-5">
        <div className="is-size-4 has-text-weight-semibold">Last few details...</div>
        <div className="has-text-weight-semibold text-green-highlight">Remember: applying will <u>not</u> affect your personal credit</div>
      </div>

      <Error />

      <p className="has-text-weight-semibold">{ user.full_name }'s Personal Address &amp; Details</p>

      <Formik
        initialValues={application.toJS()}
        validate={async (values) => {
          const errors = {}
          const addNested = (field, msg) => errors['personal_address'] = { ...errors['personal_address'], [field]: msg };

          if (!values.personal_address.address1 || values.personal_address.address1.trim().length < 2) {
            addNested('address1', '* Required');
          }

          if (poBoxRegex.test(values.personal_address.address1)) {
            addNested('address1', '* P.O. Boxes are not accepted.');
          } else if (poBoxRegex.test(values.personal_address.address2)){
            addNested('address2', '* P.O. Boxes are not accepted.');
          }

          if (!values.personal_address.city || values.personal_address.city.trim().length < 2) {
            addNested('city', '* Required');
          }

          if (!values.personal_address.state) addNested('state', '* Required');

          if (!values.personal_address.zip) {
            addNested('zip', '* Required');
          } else if(values.personal_address.zip.trim().length < 5) {
            addNested('zip', '* Must be 5 characters in length');
          }

          if (!values.ssn) {
            errors.ssn = '* Required';
          } else if (values.ssn.replace(/[^\d]/g, '').length < 9) {
            errors.ssn = '* Must be 9 characters in length';
          }

          if (!values.birthdate) {
            errors.birthdate = '* Required';
          } else if (values.birthdate.replace(/[^\d]/g, '').length < 8) {
            errors.birthdate = '* Please enter a valid date';
          }

          return errors;
        }}
        onSubmit={async (values, { setFieldError }) => {
          dispatch(loading(true));

          const bdayCheck = await checkBirthdate(values.birthdate);

          if (!bdayCheck.valid) {
            const msg_lookup = {
              owner_under_18: "You must be 18 or over to apply",
              invalid_format: "Please enter a valid date"
            };
            const message = bdayCheck.error ? msg_lookup[bdayCheck.error] : msg_lookup['invalid_format']

            setFieldError('birthdate', `* ${message}`);
          }

          if (bdayCheck.valid) {
            save(values);
          } else {
            dispatch(loading(false));
          }
        }}
      >
        {({ isSubmitting, errors }) => (
          <Form role="form">
            <ColumnInput error={errors.personal_address && errors.personal_address.address1}>
              <StickyInput
                id="personal_address.address1"
                placeholder="Personal Street Address"
              />
            </ColumnInput>

            <ColumnInput>
              <StickyInput
                id="personal_address.address2"
                placeholder="Suite or Floor"
                styles="is-mobile"
              />
            </ColumnInput>

            <ColumnInput error={errors.personal_address && errors.personal_address.city}>
              <StickyInput
                id="personal_address.city"
                placeholder="City"
              />
            </ColumnInput>

            <ColumnInput error={errors.personal_address && (errors.personal_address.state || errors.personal_address.zip)}>
              <StickySelect
                id="personal_address.state"
                placeholder="State"
                options={states}
              />

              <StickyInput
                id="personal_address.zip"
                mask="99999"
                type="tel"
                placeholder="Zip"
              />
            </ColumnInput>

            <ColumnInput error={errors.birthdate || errors.ssn}>
              <StickyInput
                className="is-half"
                id="birthdate"
                type="tel"
                placeholder="Date of Birth"
                inputMode="numeric"
                mask="99/99/9999"
              />

              <StickyInput
                id="ssn"
                type="tel"
                className="is-half"
                placeholder="SSN"
                inputMode="numeric"
                mask="999-99-9999"
                disclaimer={
                  <Tooltip
                    tooltipText="Your SSN is required to verify your identity & process your application. There is no impact on your personal credit to apply with Credit Key."
                    triggerClass="px-1 disclaimer"
                    containerClass="p-3 px-2 has-background-grey-lighter disclaimer-container"
                    placement="right"
                  >
                    <FontAwesomeIcon
                      icon={['fal', 'circle-question']}
                      className="mr-1"
                    />
                    Why are you asking for this?
                  </Tooltip>
                }
              />
            </ColumnInput>

            <p className="has-text-centered is-size-7 pt-4 mb-4">I authorize Credit Key to obtain my credit report (soft inquiry only).</p>

            <button
              type="submit"
              role="submit"
              disabled={isLoading}
              className={classNames('button is-primary is-large is-fullwidth has-text-weight-bold', {
                'is-loading': isLoading
              })}>
              {session.hasCustomFlag('vip') ? 'Submit Application' : 'Check My Rate'}
            </button>
          </Form>
        )}
      </Formik>

      <p className="is-size-7 has-text-centered mt-2">The USA Patriot Act requires all financial institutions to obtain, verify and record information that identifies every customer. Completion of this documentation is required in order to comply with the USA Patriot Act.</p>
    </div>
  )
}
